/**
 * Visual mapping utilities — resolve color / size / shape
 * from data variables with optional binning.
 */

import type { VisualMapping, MarkerShape } from './types';
import { MARKER_SHAPE_OPTIONS } from './types';

// ---------------------------------------------------------------------------
// Linear regression (for trend line / lift curve slope)
// ---------------------------------------------------------------------------

export function linearRegression(xs: number[], ys: number[]): { slope: number; intercept: number } {
  const n = xs.length;
  if (n < 2) return { slope: 0, intercept: 0 };
  const sx = xs.reduce((a, b) => a + b, 0);
  const sy = ys.reduce((a, b) => a + b, 0);
  const sxy = xs.reduce((a, x, i) => a + x * (ys[i] ?? 0), 0);
  const sx2 = xs.reduce((a, x) => a + x * x, 0);
  const slope = (n * sxy - sx * sy) / (n * sx2 - sx * sx) || 0;
  const intercept = (sy - slope * sx) / n;
  return { slope, intercept };
}

/** True if axes look like CL (y) vs alpha/angle (x) for lift curve slope label. */
export function isClAlphaAxes(xField: string, yField: string): boolean {
  const x = xField.toLowerCase();
  const y = yField.toLowerCase();
  const alphaLike = /alpha|aoa|angle|α|deg/.test(x);
  const clLike = /\bcl\b|^c_l$/.test(y) || y === 'cl';
  return alphaLike && clLike;
}

// ---------------------------------------------------------------------------
// Binning
// ---------------------------------------------------------------------------

export function binValues(values: number[], binCount: number): string[] {
  const valid = values.filter((v) => !isNaN(v));
  if (valid.length === 0) return values.map(() => '(empty)');
  const min = Math.min(...valid);
  const max = Math.max(...valid);
  if (min === max) return values.map(() => String(min));
  const step = (max - min) / binCount;
  return values.map((v) => {
    if (isNaN(v)) return '(empty)';
    const idx = Math.min(Math.floor((v - min) / step), binCount - 1);
    const lo = min + idx * step;
    const hi = lo + step;
    return `${lo.toPrecision(3)}–${hi.toPrecision(3)}`;
  });
}

export function isFieldNumeric(data: Record<string, unknown>[], field: string): boolean {
  for (const row of data) {
    const v = row[field];
    if (v == null || v === '') continue;
    if (typeof v === 'number') continue;
    if (typeof v === 'string' && !isNaN(Number(v))) continue;
    return false;
  }
  return true;
}

export function getLabels(
  data: Record<string, unknown>[],
  mapping: VisualMapping,
): string[] {
  const field = mapping.field;
  if (!field) return data.map(() => 'all');

  const rawValues = data.map((r) => r[field]);
  const numeric = isFieldNumeric(data, field);

  if (mapping.bin && numeric) {
    return binValues(
      rawValues.map((v) => Number(v)),
      mapping.binCount,
    );
  }

  return rawValues.map((v) => String(v ?? '(empty)'));
}

export function getUniqueLabels(labels: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const l of labels) {
    if (!seen.has(l)) {
      seen.add(l);
      out.push(l);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Color resolution
// ---------------------------------------------------------------------------

export interface ColorMappingResult {
  perPoint: (string | number)[];
  continuous: boolean;
  range?: [number, number];
}

export function resolveColorMapping(
  data: Record<string, unknown>[],
  mapping: VisualMapping,
  palette: string[],
): ColorMappingResult | null {
  if (mapping.mode !== 'byVariable' || !mapping.field) return null;

  const numeric = isFieldNumeric(data, mapping.field);

  if (numeric && !mapping.bin) {
    const vals = data.map((r) => Number(r[mapping.field!]));
    const min = Math.min(...vals.filter((v) => !isNaN(v)));
    const max = Math.max(...vals.filter((v) => !isNaN(v)));
    return { perPoint: vals, continuous: true, range: [min, max] };
  }

  const labels = getLabels(data, mapping);
  const unique = getUniqueLabels(labels);
  const colorMap = new Map<string, string>();
  unique.forEach((lbl, i) => colorMap.set(lbl, palette[i % palette.length]!));
  return { perPoint: labels.map((l) => colorMap.get(l)!), continuous: false };
}

// ---------------------------------------------------------------------------
// Size resolution
// ---------------------------------------------------------------------------

export interface SizeMappingResult {
  perPoint: number[];
}

export function resolveSizeMapping(
  data: Record<string, unknown>[],
  mapping: VisualMapping,
  fallbackSize: number,
  minSize = 3,
  maxSize = 30,
): SizeMappingResult | null {
  if (mapping.mode !== 'byVariable' || !mapping.field) return null;

  const numeric = isFieldNumeric(data, mapping.field);

  if (numeric && !mapping.bin) {
    const vals = data.map((r) => Number(r[mapping.field!]));
    const lo = Math.min(...vals.filter((v) => !isNaN(v)));
    const hi = Math.max(...vals.filter((v) => !isNaN(v)));
    const span = hi - lo || 1;
    return {
      perPoint: vals.map((v) =>
        isNaN(v) ? fallbackSize : minSize + ((v - lo) / span) * (maxSize - minSize),
      ),
    };
  }

  const labels = getLabels(data, mapping);
  const unique = getUniqueLabels(labels);
  const step = unique.length > 1 ? (maxSize - minSize) / (unique.length - 1) : 0;
  const sizeMap = new Map<string, number>();
  unique.forEach((lbl, i) => sizeMap.set(lbl, minSize + i * step));
  return { perPoint: labels.map((l) => sizeMap.get(l) ?? fallbackSize) };
}

// ---------------------------------------------------------------------------
// Shape resolution
// ---------------------------------------------------------------------------

export interface ShapeMappingResult {
  perPoint: string[];
  legend: Map<string, MarkerShape>;
}

export function resolveShapeMapping(
  data: Record<string, unknown>[],
  mapping: VisualMapping,
  fallbackShape: MarkerShape,
): ShapeMappingResult | null {
  if (mapping.mode !== 'byVariable' || !mapping.field) return null;

  const labels = getLabels(data, mapping);
  const unique = getUniqueLabels(labels);
  const shapePool = MARKER_SHAPE_OPTIONS;
  const shapeMap = new Map<string, MarkerShape>();
  unique.forEach((lbl, i) => shapeMap.set(lbl, shapePool[i % shapePool.length]!));

  const plotlyMap: Record<MarkerShape, string> = {
    circle: 'circle', square: 'square', diamond: 'diamond',
    triangle: 'triangle-up', cross: 'cross', x: 'x',
  };

  return {
    perPoint: labels.map((l) => plotlyMap[shapeMap.get(l) ?? fallbackShape] ?? 'circle'),
    legend: shapeMap,
  };
}
